import { getByID } from '@adornis/baseql/operations/mongo.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import {
  completeCourseAttempt,
  createNewAttempt,
  getCourseById,
  getCoursePages,
  submitPageAnswer,
} from '@nsc/simple-lms/api/lms-queries.js';
import { LMSCourseEnrollment, LMSEnrollmentStatus } from '@nsc/simple-lms/entities/LMSCourseEnrollment.js';
import { LMSCoursePageType } from '@nsc/simple-lms/entities/LMSCoursePage.js';
import { clampScorePercent } from '@nsc/simple-lms/client/score-utils.js';
import { html, nothing } from 'lit';
import * as m from '../../localization/out/messages.js';
import { router } from '../startup.js';

export const CoursePlayerPage = stateful(({ courseId, enrollmentId }: { courseId: string; enrollmentId: string }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState(false);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    value: course,
    loading: courseLoading,
    error: courseError,
  } = useQuery(() => getCourseById(courseId)({ _id: 1, title: 1, passingScorePercent: 1 }), [courseId]);

  const {
    value: pages,
    loading: pagesLoading,
    error: pagesError,
  } = useQuery(
    () =>
      getCoursePages(courseId)({
        _id: 1,
        courseId: 1,
        order: 1,
        type: 1,
        title: 1,
        videoUrl: 1,
        questionText: 1,
        answerOptions: { _id: 1, text: 1, isCorrect: 1 },
      }),
    [courseId],
  );

  const {
    value: enrollment,
    loading: enrollmentLoading,
    error: enrollmentError,
  } = useQuery(
    () =>
      getByID(
        LMSCourseEnrollment,
        enrollmentId,
      )({
        _id: 1,
        status: 1,
        lockedAt: 1,
        lockReason: 1,
        scorePercent: 1,
        attempts: {
          completedAt: 1,
          answers: { coursePageId: 1, selectedOptionIds: 1, isCorrect: 1 },
          passed: 1,
          scorePercent: 1,
        },
      }),
    [enrollmentId, refreshKey],
  );

  if (courseLoading || pagesLoading || enrollmentLoading) {
    return html`
      <div class="flex items-center justify-center h-full">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>
    `;
  }

  if (courseError) {
    return html`
      <div class="flex items-center justify-center h-full">
        <p class="text-error">${m.player_error_course()}: ${courseError.message}</p>
      </div>
    `;
  }

  if (pagesError) {
    return html`
      <div class="flex items-center justify-center h-full">
        <p class="text-error">${m.player_error_pages()}: ${pagesError.message}</p>
      </div>
    `;
  }

  if (enrollmentError) {
    return html`
      <div class="flex items-center justify-center h-full">
        <p class="text-error">${m.player_error_enrollment()}: ${enrollmentError.message}</p>
      </div>
    `;
  }

  if (!enrollment) {
    return html`
      <div class="flex items-center justify-center h-full">
        <p class="text-base-content/60">${m.player_not_assigned()}</p>
      </div>
    `;
  }

  if (!pages || pages.length === 0) {
    return html`
      <div class="flex items-center justify-center h-full">
        <p class="text-base-content/60">${m.player_no_content()}</p>
      </div>
    `;
  }

  if (!course) {
    return html`
      <div class="flex items-center justify-center h-full">
        <p class="text-base-content/60">${m.player_not_found()}</p>
      </div>
    `;
  }

  const attempts = enrollment.attempts ?? [];
  const lastAttempt = attempts[attempts.length - 1];
  const hasIncompleteAttempt = lastAttempt && !lastAttempt.completedAt;

  const isCompleted =
    (enrollment.status === LMSEnrollmentStatus.PASSED || enrollment.status === LMSEnrollmentStatus.FAILED) &&
    !hasIncompleteAttempt;

  if (isCompleted) {
    const passed = enrollment.attempts?.at(-1)?.passed;
    const isLocked = !!enrollment.lockedAt;

    const handleRetry = async () => {
      if (resetting) return;
      setResetting(true);
      setRetryError(null);
      try {
        await createNewAttempt(enrollmentId)({ _id: 1, status: 1, attempts: { completedAt: 1 } });
        setCurrentIndex(0);
        setSelectedOptions([]);
        setAnswerSubmitted(false);
        setAnswerCorrect(false);
        setRefreshKey(refreshKey + 1);
      } catch (error) {
        setRetryError((error as Error)?.message ?? m.course_retry_failed());
      }
      setResetting(false);
    };

    return html`
      <div class="px-4 py-6 max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[60vh] text-center">
        <span
          class="${passed
            ? 'icon-[material-symbols--check-circle-outline] text-success'
            : 'icon-[material-symbols--error-outline] text-error'} text-6xl mb-4"
        ></span>
        <h2 class="text-2xl font-bold mb-2">${passed ? m.player_passed() : m.player_failed()}</h2>
        <p class="text-base-content/60 mb-2">
          ${m.player_score_needed({
            score: clampScorePercent(enrollment.scorePercent),
            required: course.passingScorePercent ?? 0,
          })}
        </p>
        ${passed
          ? html`
              <a href="${router.link('courseDetail', { params: { courseId } })}" class="btn btn-primary mt-4">
                ${m.player_back_to_course()}
              </a>
            `
          : html`
              <div class="flex flex-col sm:flex-row gap-3 mt-4">
                <a href="${router.link('academy', {})}" class="btn btn-ghost">${m.nav_academy()}</a>
                <button class="btn btn-primary" ?disabled=${resetting || isLocked} @click=${handleRetry}>
                  ${resetting
                    ? html`<span class="loading loading-spinner loading-sm"></span>`
                    : isLocked
                      ? m.course_max_attempts_reached()
                      : m.player_retry()}
                </button>
              </div>
            `}
        ${isLocked
          ? html`<p class="text-sm text-error mt-3">${enrollment.lockReason ?? m.player_course_locked_fallback()}</p>`
          : ''}
        ${retryError ? html`<p class="text-sm text-error mt-2">${retryError}</p>` : ''}
      </div>
    `;
  }

  // No attempt is open and the enrollment is not finished either — reachable by opening a
  // bookmarked player URL, going back into the player after a reset, or landing here without
  // passing through the detail page's start button. Rendering the quiz here means every answer
  // submit is rejected server-side ("No in-progress attempt found"), so offer to open an attempt.
  if (!hasIncompleteAttempt) {
    const handleStartAttempt = async () => {
      if (resetting) return;
      setResetting(true);
      setRetryError(null);
      try {
        await createNewAttempt(enrollmentId)({ _id: 1, status: 1, attempts: { completedAt: 1 } });
        setCurrentIndex(0);
        setSelectedOptions([]);
        setAnswerSubmitted(false);
        setAnswerCorrect(false);
        setRefreshKey(refreshKey + 1);
      } catch (error) {
        setRetryError((error as Error)?.message ?? m.course_start_failed());
      }
      setResetting(false);
    };

    return html`
      <div class="px-4 py-6 max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[60vh] text-center gap-2">
        <span class="icon-[material-symbols--play-circle-outline] text-5xl text-base-content/40"></span>
        <h2 class="text-xl font-bold">${m.player_no_attempt_title()}</h2>
        <p class="text-base-content/60">${m.player_no_attempt_body()}</p>
        <div class="flex flex-col sm:flex-row gap-3 mt-4">
          <a href="${router.link('courseDetail', { params: { courseId } })}" class="btn btn-ghost">
            ${m.player_back_to_course()}
          </a>
          <button class="btn btn-primary" ?disabled=${resetting} @click=${handleStartAttempt}>
            ${resetting ? html`<span class="loading loading-spinner loading-sm"></span>` : m.player_start_attempt()}
          </button>
        </div>
        ${retryError ? html`<p class="text-sm text-error mt-2">${retryError}</p>` : ''}
      </div>
    `;
  }

  const currentPage = pages[currentIndex];
  if (!currentPage) return nothing;

  const isLastPage = currentIndex === pages.length - 1;
  const isQuestion = currentPage.type === LMSCoursePageType.QUESTION;
  const currentAnswers = (lastAttempt?.completedAt ? undefined : lastAttempt?.answers) ?? [];
  const existingAnswer = currentAnswers.find(a => a.coursePageId === currentPage._id);
  const showPreviousAnswer = !!existingAnswer && !answerSubmitted;

  const handleOptionToggle = (optionId: string) => {
    if (answerSubmitted || showPreviousAnswer) return;
    const current = [...selectedOptions];
    const idx = current.indexOf(optionId);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(optionId);
    setSelectedOptions(current);
  };

  const handleSubmitAnswer = async () => {
    if (!currentPage._id || submittingAnswer || !hasIncompleteAttempt) return;
    setSubmittingAnswer(true);
    try {
      const result = await submitPageAnswer(
        enrollmentId,
        currentPage._id,
        selectedOptions,
      )({ _id: 1, attempts: { completedAt: 1, passed: 1, answers: { coursePageId: 1, isCorrect: 1 } } });
      const lastResult = (result.attempts ?? []).at(-1);
      const submitted = (lastResult?.answers ?? []).find(a => a.coursePageId === currentPage._id);
      setAnswerSubmitted(true);
      setAnswerCorrect(submitted?.isCorrect ?? false);
      setRefreshKey(refreshKey + 1);
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleNext = () => {
    setSelectedOptions([]);
    setAnswerSubmitted(false);
    setAnswerCorrect(false);
    setCurrentIndex(currentIndex + 1);
  };

  const handleComplete = async () => {
    if (completing || !hasIncompleteAttempt) return;
    setCompleting(true);
    await completeCourseAttempt(enrollmentId)({ _id: 1, status: 1, scorePercent: 1 });
    setRefreshKey(refreshKey + 1);
    setCompleting(false);
  };

  const canProceed = isQuestion ? answerSubmitted || showPreviousAnswer : true;

  return html`
    <div class="flex flex-col h-full max-w-2xl mx-auto">
      <div class="flex items-center justify-between px-4 py-3 border-b border-base-300 bg-base-100 sticky top-0 z-10">
        <a href="${router.link('courseDetail', { params: { courseId } })}" class="btn btn-ghost btn-sm btn-circle">
          <span class="icon-[material-symbols--arrow-back] text-xl"></span>
        </a>
        <span class="text-sm font-medium text-base-content/60"> ${currentIndex + 1} / ${pages.length} </span>
      </div>

      <progress
        class="progress progress-primary w-full rounded-none h-1.5"
        value="${currentIndex + 1}"
        max="${pages.length}"
      ></progress>

      <div class="flex-1 overflow-y-auto px-4 py-5">
        ${currentPage.type === LMSCoursePageType.VIDEO
          ? html`
              <h3 class="text-xl font-semibold mb-4">${currentPage.title ?? m.course_content_title_default_video()}</h3>
              ${currentPage.videoUrl
                ? html`
                    <div class="aspect-video bg-base-300 rounded-xl overflow-hidden shadow-sm">
                      <video
                        controls
                        class="w-full h-full"
                        src="${
                          // TODO this is hacky, we should be able to create serve links for sibling deployments as well via engine
                          AdornisFile.getServeLink(currentPage.videoUrl)
                            .replace('smartteams-app', 'smartteams')
                            .replace('smart-teams-app', 'smart-teams')
                        }"
                      ></video>
                    </div>
                  `
                : html`<p class="text-base-content/50">${m.player_no_video()}</p>`}
            `
          : html`
              <h3 class="text-xl font-semibold mb-2">
                ${currentPage.title ?? m.course_content_title_default_question()}
              </h3>
              ${currentPage.questionText
                ? html`<p class="text-base-content/70 mb-5">${currentPage.questionText}</p>`
                : ''}

              <div class="flex flex-col gap-3">
                ${(currentPage.answerOptions ?? []).map(option => {
                  const isSelected = showPreviousAnswer
                    ? (existingAnswer?.selectedOptionIds?.includes(option._id!) ?? false)
                    : selectedOptions.includes(option._id!);
                  const showResult = answerSubmitted || showPreviousAnswer;
                  const isCorrectOption = option.isCorrect;

                  let optionClass = 'border-base-300 bg-base-100';
                  if (showResult && isCorrectOption) optionClass = 'border-success bg-success/10';
                  else if (showResult && isSelected && !isCorrectOption) optionClass = 'border-error bg-error/10';
                  else if (isSelected) optionClass = 'border-primary bg-primary/10';

                  return html`
                    <button
                      class="btn btn-outline justify-start text-left h-auto py-3.5 px-4 rounded-xl font-normal ${optionClass}"
                      ?disabled=${answerSubmitted || showPreviousAnswer || submittingAnswer}
                      @click=${() => handleOptionToggle(option._id!)}
                    >
                      <span class="flex-1">${option.text}</span>
                      ${showResult && isCorrectOption
                        ? html`<span class="icon-[material-symbols--check-circle] text-success text-xl"></span>`
                        : ''}
                      ${showResult && isSelected && !isCorrectOption
                        ? html`<span class="icon-[material-symbols--cancel] text-error text-xl"></span>`
                        : ''}
                    </button>
                  `;
                })}
              </div>

              ${isQuestion && !answerSubmitted && !showPreviousAnswer
                ? html`
                    <button
                      class="btn btn-primary w-full mt-5 rounded-xl"
                      ?disabled=${selectedOptions.length === 0 || submittingAnswer}
                      @click=${handleSubmitAnswer}
                    >
                      ${submittingAnswer ? html`<span class="loading loading-spinner loading-sm"></span>` : ''}
                      ${m.player_submit_answer()}
                    </button>
                  `
                : ''}
              ${answerSubmitted
                ? html`
                    <div class="alert ${answerCorrect ? 'alert-success' : 'alert-error'} mt-5 rounded-xl">
                      ${answerCorrect ? m.player_answer_correct() : m.player_answer_incorrect()}
                    </div>
                  `
                : ''}
              ${showPreviousAnswer
                ? html`
                    <div class="alert ${existingAnswer?.isCorrect ? 'alert-success' : 'alert-error'} mt-5 rounded-xl">
                      ${existingAnswer?.isCorrect
                        ? m.player_answer_correct_already()
                        : m.player_answer_incorrect_already()}
                    </div>
                  `
                : ''}
            `}
      </div>

      <div class="flex items-center justify-between px-4 py-3 border-t border-base-300 bg-base-100">
        ${currentIndex > 0
          ? html`
              <button
                class="btn btn-ghost"
                @click=${() => {
                  setSelectedOptions([]);
                  setAnswerSubmitted(false);
                  setAnswerCorrect(false);
                  setCurrentIndex(currentIndex - 1);
                }}
              >
                <span class="icon-[material-symbols--arrow-back] text-lg"></span>
                ${m.course_back()}
              </button>
            `
          : html`<div></div>`}
        ${isLastPage
          ? html`
              <button
                class="btn btn-success rounded-xl"
                ?disabled=${!canProceed || completing}
                @click=${handleComplete}
              >
                ${completing
                  ? html`<span class="loading loading-spinner loading-sm"></span>`
                  : m.player_complete_course()}
              </button>
            `
          : html`
              <button class="btn btn-primary rounded-xl" ?disabled=${!canProceed} @click=${handleNext}>
                ${m.course_continue()} <span class="icon-[material-symbols--arrow-forward] text-lg"></span>
              </button>
            `}
      </div>
    </div>
  `;
});
